import "@lit-labs/virtualizer";
import {
    type Employee,
    employeeNameOrderForSort,
    EmployeeStatus,
    employeeStatusColor,
    employeeStatusLabel,
    getEmployeeName,
} from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../components/avatar/avatar";
import { app } from "../init";
import { StateMixin } from "../mixins/state";
import { shared } from "../styles";
import { Checkbox } from "./checkbox";

@customElement("ptc-employee-list")
export class EmployeeList extends StateMixin(LitElement) {
    @property({ attribute: false })
    employee: Employee | null = null;

    @property({ type: Boolean, reflect: true, attribute: "has-next" })
    hasNext = false;

    @property({ type: Boolean, reflect: true, attribute: "has-previous" })
    hasPrevious = false;

    @state()
    private _filterString = "";

    @state()
    private _employees: Employee[] = [];

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("click", (e: MouseEvent) => e.stopPropagation());
    }

    clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    focus() {
        this._filterInput.focus();
    }

    appLoaded() {
        this._applyFilter();
    }

    stateChanged() {
        this._applyFilter();
    }

    private _selectEmployee(employee: Employee) {
        this.employee = employee;
        this.dispatchEvent(new CustomEvent("change", { detail: { employee } }));
    }

    private _updateFilters() {
        this._filterString = this._filterInput.value;
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            ptc-ui-icon.filter-icon {
                pointer-events: auto !important;
            }

            .filter-icon:hover {
                cursor: pointer;
                opacity: 0.7;
            }

            .filter-icon:active,
            .filter-icon[active] {
                color: var(--color-primary);
            }

            .filter-item {
                padding: 0.4em 0.7em;
                border-radius: var(--border-radius);
            }

            .vertical.tabs button {
                border-radius: 0px;
                padding: 0.5em;
            }

            .vertical.tabs button:not(:last-child) {
                border-bottom: solid 1px var(--shade-2);
            }

            .employee-item {
                width: 100%;
            }
        `,
    ];

    updated(changes: Map<string, unknown>) {
        if (changes.has("_filterString")) {
            this._applyFilter();
        } else if (changes.has("employee")) {
            this._updateNavigation();
        }
    }

    private _applyFilter() {
        const base = this._filterString ? app.accessibleEmployees : app.activeAccessibleEmployees;
        this._employees = base.filter(
            (e) =>
                `${e.name} ${e.staffNumber}`.toLowerCase().includes(this._filterString.toLocaleLowerCase()) &&
                e.companyId === app.company?.id
        );
        this._updateNavigation();
    }

    private _updateNavigation() {
        const index = this._currentIndex;
        this.hasPrevious = index > 0;
        this.hasNext = index >= 0 && index < this._employees.length - 1;
        this.dispatchEvent(new CustomEvent("navigation-changed"));
    }

    public selectNext() {
        const nextIndex = this._currentIndex + 1;
        if (nextIndex < this._employees.length) {
            this._selectEmployee(this._employees[nextIndex]);
        }
    }

    public selectPrevious() {
        const previousIndex = this._currentIndex - 1;
        if (previousIndex >= 0) {
            this._selectEmployee(this._employees[previousIndex]);
        }
    }

    private get _currentIndex() {
        return this._employees.findIndex((e) => e.id === this.employee?.id);
    }

    render() {
        const employees = this._employees;

        return html`
            <div class="fullbleed vertical layout">
                <div class="margined filter-input right icon input">
                    <input
                        id="filterInput"
                        type="text"
                        placeholder="Suchen..."
                        @input=${this._updateFilters}
                        autocomplete="off"
                    />
                    ${this._filterString
                        ? html`<ptc-ui-icon
                              name="icon.action.clear"
                              class="click icon"
                              @click=${this.clearFilter}
                          ></ptc-ui-icon>`
                        : html`<ptc-ui-icon
                              name="icon.action.search"
                              class="icon"
                              @click=${this.clearFilter}
                          ></ptc-ui-icon>`}
                </div>

                <lit-virtualizer
                    class="stretch relative"
                    scroller
                    .items=${employees}
                    .renderItem=${(e: Employee) => html`
                        <div
                            class="employee-item padded spacing border-bottom horizontal start-aligning layout click hover"
                            @click=${() => this._selectEmployee(e)}
                            ?active=${this.employee && this.employee.id === e.id}
                        >
                            <ptc-avatar .employee=${e} class="smaller"></ptc-avatar>
                            <div class="stretch collapse">
                                <div class="horizonal center-aligning spacing layout">
                                    <div class="ellipsis stretch">
                                        ${getEmployeeName(
                                            e,
                                            employeeNameOrderForSort(app.employeeFilters.sortProperty)
                                        )}
                                    </div>
                                    ${e.staffNumber ? html`<div class="tiny subtle">#${e.staffNumber}</div>` : ""}
                                </div>

                                <div class="tinier top-margined spacing horizontal layout">
                                    ${e.status !== EmployeeStatus.Active
                                        ? html`
                                              <div class="${employeeStatusColor(e.status)} inverted pill">
                                                  ${employeeStatusLabel(e.status)}
                                              </div>
                                          `
                                        : ""}
                                    ${e.positions.length
                                        ? e.positions
                                              .slice(0, 1)
                                              .map(
                                                  (position) => html`
                                                      <div
                                                          class="pill ellipsis"
                                                          style="--color-highlight: ${app.getPositionColor(
                                                              position
                                                          )}; max-width: 8em;"
                                                      >
                                                          ${position.name}
                                                      </div>
                                                  `
                                              )
                                        : html` <div class="pill ellipsis">Ohne Arbeitsbereich</div> `}
                                    ${e.positions.length > 1
                                        ? html` <div class="pill">+ ${e.positions.length - 1}</div> `
                                        : ""}
                                </div>
                            </div>
                        </div>
                    `}
                >
                </lit-virtualizer>
            </div>
        `;
    }
}
